import { appModulesToRequiredUserRoles } from "@jugl-web/domain-resources/common/consts";
import { useTaskBoardList } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardList";
import { BoardAvatar } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { useResizableSidebar } from "@web-src/components/ResizableSidebar";
import {
  SIDEBAR_EXPANDED_KEY,
  SIDEBAR_MANUAL_WIDTH,
} from "@jugl-web/utils/storage";
import { useUnreadIndicators } from "@web-src/components/UnreadIndicatorsProvider/useUnreadIndicators";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { isAppInDevMode } from "@web-src/modules/common/utils/isAppInDevMode";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useFCM } from "@web-src/modules/notifications/providers/FCMProvider/useFCM";
import { useTheme } from "@web-src/themes/useTheme";
import { useUserEntityPreferences } from "@jugl-web/domain-resources/preferences/hooks/useUserEntityPreferences";
import { useMemo } from "react";
import {
  AppMenuItem,
  UserRole,
  SubscriptionPlanModuleId,
} from "@jugl-web/rest-api";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { TabRoute } from "../../../types";
import { SidebarItem } from "../types";

import { ReactComponent as DashboardIconLight } from "../assets/dashboard-light.svg";
import { ReactComponent as DashboardIconDark } from "../assets/dashboard-dark.svg";
import { ReactComponent as DashboardIconBlue } from "../assets/dashboard-blue.svg";

import { ReactComponent as ChatsIconBlue } from "../assets/chats-blue.svg";
import { ReactComponent as ChatsIconDark } from "../assets/chats-dark.svg";
import { ReactComponent as ChatsIconLight } from "../assets/chats-light.svg";

import { ReactComponent as PeopleIconBlue } from "../assets/people-blue.svg";
import { ReactComponent as PeopleIconDark } from "../assets/people-dark.svg";
import { ReactComponent as PeopleIconLight } from "../assets/people-light.svg";

import { ReactComponent as ClientsIconBlue } from "../assets/clients-blue.svg";
import { ReactComponent as ClientsIconDark } from "../assets/clients-dark.svg";
import { ReactComponent as ClientsIconLight } from "../assets/clients-light.svg";

import { ReactComponent as CallsIconBlue } from "../assets/calls-blue.svg";
import { ReactComponent as CallsIconDark } from "../assets/calls-dark.svg";
import { ReactComponent as CallsIconLight } from "../assets/calls-light.svg";

import { ReactComponent as DriveIconBlue } from "../assets/drive-blue.svg";
import { ReactComponent as DriveIconDark } from "../assets/drive-dark.svg";
import { ReactComponent as DriveIconLight } from "../assets/drive-light.svg";

import { ReactComponent as OrderFormsIconBlue } from "../assets/order-forms-blue.svg";
import { ReactComponent as OrderFormsIconDark } from "../assets/order-forms-dark.svg";
import { ReactComponent as OrderFormsIconLight } from "../assets/order-forms-light.svg";

import { ReactComponent as ScheduleIconBlue } from "../assets/schedule-blue.svg";
import { ReactComponent as ScheduleIconDark } from "../assets/schedule-dark.svg";
import { ReactComponent as ScheduleIconLight } from "../assets/schedule-light.svg";

import { ReactComponent as WorkspaceIconBlue } from "../assets/workspace-blue.svg";
import { ReactComponent as WorkspaceIconDark } from "../assets/workspace-dark.svg";
import { ReactComponent as WorkspaceIconLight } from "../assets/workspace-light.svg";

import { ReactComponent as IntegrationsIconLight } from "../assets/integrations-light.svg";
import { ReactComponent as IntegrationsIconDark } from "../assets/integrations-dark.svg";
import { ReactComponent as IntegrationsIconBlue } from "../assets/integrations-blue.svg";

import { ReactComponent as InventoryIconBlue } from "../assets/inventory-blue.svg";
import { ReactComponent as InventoryIconDark } from "../assets/inventory-dark.svg";
import { ReactComponent as InventoryIconLight } from "../assets/inventory-light.svg";

import { ReactComponent as InvoicesIconBlue } from "../assets/invoices-blue.svg";
import { ReactComponent as InvoicesIconDark } from "../assets/invoices-dark.svg";
import { ReactComponent as InvoicesIconLight } from "../assets/invoices-light.svg";

import { ReactComponent as ClientSupportIconBlue } from "../assets/client-support-blue.svg";
import { ReactComponent as ClientSupportIconDark } from "../assets/client-support-dark.svg";
import { ReactComponent as ClientSupportIconLight } from "../assets/client-support-light.svg";

import { ReactComponent as MyTasksIconBlue } from "../assets/my-tasks-blue.svg";
import { ReactComponent as MyTasksIconLight } from "../assets/my-tasks-light.svg";
import { ReactComponent as MyTasksIconDark } from "../assets/my-tasks-dark.svg";

import { ReactComponent as TeamTasksIconBlue } from "../assets/team-tasks-blue.svg";
import { ReactComponent as TeamTasksIconLight } from "../assets/team-tasks-light.svg";
import { ReactComponent as TeamTasksIconDark } from "../assets/team-tasks-dark.svg";

import { ReactComponent as BoardAutomationBlue } from "../assets/board-automation-blue.svg";
import { ReactComponent as BoardAutomationLight } from "../assets/board-automation-light.svg";
import { ReactComponent as BoardAutomationDark } from "../assets/board-automation-dark.svg";

import { getAccessibleSidebarItems } from "../utils";

export const useHomeSidebar = () => {
  const {
    isSidebarExpanded,
    sidebarWidth,
    updateIsSidebarExpanded,
    updateSidebarWidth,
  } = useResizableSidebar({
    expandedStorageKey: SIDEBAR_EXPANDED_KEY,
    widthStorageKey: SIDEBAR_MANUAL_WIDTH,
  });

  const { entity, subscriptionInfo } = useEntitySelectedProvider();
  const { customTheme } = useTheme();
  const { preferences: userPreferences } = useUserEntityPreferences({
    entityId: entity.id,
  });

  const pinnedMenuTools = useMemo(
    () => userPreferences?.pinned_modules?.pinnedTools || [],
    [userPreferences?.pinned_modules?.pinnedTools]
  );
  const { me } = useMe();

  const { accessibleBoards: boards } = useTaskBoardList({
    entityId: entity.id,
    meId: me?.id as string,
  });
  const { moduleUnreadIndicators } = useUnreadIndicators();
  const { totalUnreadCount: crmUnreadCount } = useCRMUnreadIndicators();

  const { logEvent } = useFCM();
  const { generateUrl } = useNavigation();
  const { t } = useTranslations();

  // TODO: come up with a better way to handle this
  const activeTab = window.location.pathname.split("/")[2] || "";
  const activePath = window.location.pathname.split("/").slice(2).join("/");

  const allItems = useMemo<SidebarItem[]>(() => {
    const chatItem: SidebarItem = {
      id: "chats",
      type: "link",
      SvgIcon: {
        light: ChatsIconLight,
        dark: ChatsIconDark,
        blue: ChatsIconBlue,
      }[customTheme],
      dialogIcon: ChatsIconLight,
      title: t({
        id: "home-sidebar-component.chats",
        defaultMessage: "Chats",
      }),
      active: activeTab === "chats",
      unreadCount: moduleUnreadIndicators?.chat,
      requiredUserRoles: appModulesToRequiredUserRoles.chat,
      href: `/${entity.id}/chats`,
      onClick: () => {
        logEvent("navigation_chat");
      },
      itemKey: "chats",
    };

    const myTasksItem: SidebarItem = {
      id: "my-tasks",
      type: "link",
      href: generateUrl("tasksTasks", { boardId: "my" }),
      SvgIcon: {
        light: MyTasksIconLight,
        dark: MyTasksIconDark,
        blue: MyTasksIconBlue,
      }[customTheme],
      dialogIcon: MyTasksIconLight,
      title: t({
        id: "home-sidebar-component.my-tasks",
        defaultMessage: "My Tasks",
      }),
      active: activeTab === TabRoute.tasks && activePath === "tasks/board/my",
      unreadCount: moduleUnreadIndicators?.task.my,
      requiredUserRoles: appModulesToRequiredUserRoles.tasks,
      itemKey: "tasks:my",
    };

    const teamTasksItem: SidebarItem = {
      id: "team-tasks",
      type: "link",
      href: generateUrl("tasksTasks", { boardId: "team" }),
      title: t({
        id: "home-sidebar-component.team-tasks",
        defaultMessage: "Team Tasks",
      }),
      dialogIcon: TeamTasksIconLight,
      active: activeTab === TabRoute.tasks && activePath === "tasks/board/team",
      SvgIcon: {
        light: TeamTasksIconLight,
        dark: TeamTasksIconDark,
        blue: TeamTasksIconBlue,
      }[customTheme],
      requiredUserRoles: appModulesToRequiredUserRoles.tasks,
      itemKey: "tasks:team",
    };

    const dashboardItem: SidebarItem = {
      id: "dashboard",
      type: "link",
      SvgIcon: {
        light: DashboardIconLight,
        dark: DashboardIconDark,
        blue: DashboardIconBlue,
      }[customTheme],
      dialogIcon: DashboardIconLight,
      title: t({
        id: "home-sidebar-component.dashboard",
        defaultMessage: "Dashboard",
      }),
      active: activeTab === "dashboard",
      requiredUserRoles: appModulesToRequiredUserRoles.dashboard,
      href: `/${entity.id}/dashboard`,
      itemKey: "dashboard",
      requiredEnabledModules: [SubscriptionPlanModuleId.dashboard],
    };

    const driveItem: SidebarItem = {
      id: "drive",
      type: "link",
      SvgIcon: {
        light: DriveIconLight,
        dark: DriveIconDark,
        blue: DriveIconBlue,
      }[customTheme],
      dialogIcon: DriveIconLight,
      title: t({
        id: "home-sidebar-component.drive",
        defaultMessage: "Drive",
      }),
      active: activeTab === "drive",
      unreadCount: moduleUnreadIndicators?.drive,
      requiredUserRoles: appModulesToRequiredUserRoles.drive,
      href: `/${entity.id}/drive`,
      itemKey: "drive",
      requiredEnabledModules: [SubscriptionPlanModuleId.drive],
    };

    const callsItem: SidebarItem = {
      id: "calls",
      type: "link",
      SvgIcon: {
        light: CallsIconLight,
        dark: CallsIconDark,
        blue: CallsIconBlue,
      }[customTheme],
      dialogIcon: CallsIconLight,
      title: t({
        id: "home-sidebar-component.calls",
        defaultMessage: "Calls",
      }),
      active: activeTab === "calls",
      unreadCount: moduleUnreadIndicators?.call,
      requiredUserRoles: appModulesToRequiredUserRoles.calls,
      href: `/${entity.id}/calls`,
      itemKey: "calls",
    };

    const travelLogItem: SidebarItem = {
      id: "travel-log",
      type: "link",
      SvgIcon: {
        light: ScheduleIconLight,
        dark: ScheduleIconDark,
        blue: ScheduleIconBlue,
      }[customTheme],
      dialogIcon: ScheduleIconLight,
      title: t({
        id: "home-sidebar-component.travel-log",
        defaultMessage: "Travel Log",
      }),
      active: activeTab === "travel-log",
      requiredUserRoles: appModulesToRequiredUserRoles.travelLog,
      href: `/${entity.id}/travel-log`,
      onClick: () => {
        logEvent("navigation_travel_log");
      },
      itemKey: "travelLog",
      requiredEnabledModules: [SubscriptionPlanModuleId.travelLog],
    };

    const clientSupportItem: SidebarItem = {
      id: "crm-inbox",
      type: "link",
      SvgIcon: {
        light: ClientSupportIconLight,
        dark: ClientSupportIconDark,
        blue: ClientSupportIconBlue,
      }[customTheme],
      dialogIcon: ClientSupportIconLight,
      title: t({
        id: "home-sidebar-component.crm-inbox",
        defaultMessage: "CRM Inbox",
      }),
      active: activeTab === "client-support",
      unreadCount: crmUnreadCount,
      requiredUserRoles: appModulesToRequiredUserRoles.clientSupport,
      href: generateUrl("clientSupport"),
      itemKey: "CrmInbox",
      requiredEnabledModules: [SubscriptionPlanModuleId.whatsappCrm],
    };

    const clientsItem: SidebarItem = {
      id: "customers",
      type: "link",
      SvgIcon: {
        light: ClientsIconLight,
        dark: ClientsIconDark,
        blue: ClientsIconBlue,
      }[customTheme],
      dialogIcon: ClientsIconLight,
      title: t({
        id: "home-sidebar-component.Customers",
        defaultMessage: "Customers",
      }),
      active: activeTab === "customers",
      unreadCount: moduleUnreadIndicators?.customer,
      requiredUserRoles: appModulesToRequiredUserRoles.clients,
      href: `/${entity.id}/customers`,
      itemKey: "clients",
      requiredEnabledModules: [SubscriptionPlanModuleId.clients],
    };

    const automationItem: SidebarItem = {
      id: "integrations",
      type: "link",
      SvgIcon: {
        light: IntegrationsIconLight,
        dark: IntegrationsIconDark,
        blue: IntegrationsIconBlue,
      }[customTheme],
      dialogIcon: IntegrationsIconLight,
      title: t({
        id: "home-sidebar-component.integrations",
        defaultMessage: "Integrations",
      }),
      active: activeTab === "integrations",
      requiredUserRoles: [UserRole.admin],
      href: generateUrl("integrations"),
      itemKey: "integrations",
      requiredEnabledModules: [SubscriptionPlanModuleId.integration],
    };

    const orderFormsItem: SidebarItem = {
      id: "forms",
      type: "link",
      SvgIcon: {
        light: OrderFormsIconLight,
        dark: OrderFormsIconDark,
        blue: OrderFormsIconBlue,
      }[customTheme],
      dialogIcon: OrderFormsIconLight,
      title: t({
        id: "home-sidebar-component.forms",
        defaultMessage: "Forms",
      }),
      active: activeTab === "orders",
      requiredUserRoles: appModulesToRequiredUserRoles.orderForms,
      href: `/${entity.id}/orders/forms`,
      itemKey: "orderForms",
      requiredEnabledModules: [SubscriptionPlanModuleId.orderForm],
    };

    const inventoryItem: SidebarItem = {
      id: "catalog",
      type: "link",
      SvgIcon: {
        light: InventoryIconLight,
        dark: InventoryIconDark,
        blue: InventoryIconBlue,
      }[customTheme],
      dialogIcon: InventoryIconLight,
      title: t({
        id: "home-sidebar-component.catalog",
        defaultMessage: "Catalog",
      }),
      active: activeTab === "inventory",
      requiredUserRoles: appModulesToRequiredUserRoles.inventory,
      href: generateUrl("inventoryItems"),
      itemKey: "catalog",
      requiredEnabledModules: [SubscriptionPlanModuleId.inventory],
    };

    const invoicesItem: SidebarItem = {
      id: "invoices",
      type: "link",
      SvgIcon: {
        light: InvoicesIconLight,
        dark: InvoicesIconDark,
        blue: InvoicesIconBlue,
      }[customTheme],
      dialogIcon: InvoicesIconLight,
      title: t({
        id: "home-sidebar-component.invoices",
        defaultMessage: "Invoices",
      }),
      requiredUserRoles: appModulesToRequiredUserRoles.invoices,
      href: generateUrl("invoices"),
      active: activeTab === "invoices",
      itemKey: "invoices",
      requiredEnabledModules: [SubscriptionPlanModuleId.invoices],
    };

    const boardAutomationItem: SidebarItem = {
      id: "board-automation",
      type: "link",
      SvgIcon: {
        light: BoardAutomationLight,
        dark: BoardAutomationDark,
        blue: BoardAutomationBlue,
      }[customTheme],
      dialogIcon: BoardAutomationLight,
      title: t({
        id: "home-sidebar-component.ai-workflows",
        defaultMessage: "AI Workflows",
      }),
      requiredUserRoles: appModulesToRequiredUserRoles.waAutomation,
      href: generateUrl("boardAutomation"),
      active: activeTab === "board-automation",
      requiredEnabledModules: [SubscriptionPlanModuleId.aiAgents],
      itemKey: "boardAutomation",
    };

    const teamMembersItem: SidebarItem = {
      id: "team-members",
      type: "link",
      SvgIcon: {
        light: PeopleIconLight,
        dark: PeopleIconDark,
        blue: PeopleIconBlue,
      }[customTheme],
      dialogIcon: PeopleIconLight,
      title: t({
        id: "home-sidebar-component.team-members",
        defaultMessage: "People",
      }),
      active: activeTab === "people",
      unreadCount: moduleUnreadIndicators?.people,
      requiredUserRoles: appModulesToRequiredUserRoles.people,
      href: `/${entity?.id}/people`,
      itemKey: "people",
    };

    const workspaceItem: SidebarItem = {
      id: "workspace-settings",
      type: "link",
      SvgIcon: {
        light: WorkspaceIconLight,
        dark: WorkspaceIconDark,
        blue: WorkspaceIconBlue,
      }[customTheme],
      dialogIcon: WorkspaceIconLight,
      title: t({
        id: "home-sidebar-component.workspace-settings",
        defaultMessage: "Workspace Settings",
      }),
      active: activeTab.includes("workspace"),
      requiredUserRoles: appModulesToRequiredUserRoles.workspace,
      href: generateUrl("workspaceFeatures"),
      itemKey: "workspaceSettings",
    };

    const items = [
      chatItem,
      myTasksItem,
      teamTasksItem,
      dashboardItem,
      inventoryItem,
      clientSupportItem,
      orderFormsItem,
      driveItem,
      callsItem,
      invoicesItem,
      automationItem,
      boardAutomationItem,
      clientsItem,
      travelLogItem,
      teamMembersItem,
      workspaceItem,
    ];

    const accessibleItems = getAccessibleSidebarItems(
      items,
      entity.role,
      subscriptionInfo
    );

    return accessibleItems;
  }, [
    customTheme,
    t,
    activeTab,
    moduleUnreadIndicators?.chat,
    moduleUnreadIndicators?.task.my,
    moduleUnreadIndicators?.drive,
    moduleUnreadIndicators?.customer,
    moduleUnreadIndicators?.people,
    moduleUnreadIndicators?.call,
    crmUnreadCount,
    entity.id,
    entity.role,
    generateUrl,
    activePath,
    logEvent,
    subscriptionInfo,
  ]);

  const debugItem = useMemo<SidebarItem>(
    () => ({
      type: "accordion",
      customIcon: <div>⚙️</div>,
      id: "debug",
      title: "Debug",
      itemKey: "_debug",
      items: [
        {
          type: "link",
          id: "debug-user-settings",
          title: "User settings",
          active: activePath === "debug/user-settings",
          href: `/${entity.id}/debug/user-settings`,
          itemKey: "_debug:userSettings",
        },
        {
          type: "link",
          id: "debug-subscription",
          title: "Subscription",
          active: activePath === "debug/subscription",
          href: `/${entity.id}/debug/subscription`,
          itemKey: "_debug:subscription",
        },
        {
          type: "link",
          id: "debug-subscription-v2",
          title: "Subscription V2",
          active: activePath === "debug/subscription-v2",
          href: `/${entity.id}/debug/subscription-v2`,
          itemKey: "_debug:subscription-v2",
        },
        {
          type: "link",
          id: "debug-mobile-app",
          title: "Mobile app",
          active: activePath === "debug/mobile-app",
          href: `/${entity.id}/debug/mobile-app`,
          itemKey: "_debug:mobileApp",
        },
      ],
    }),
    [entity.id, activePath]
  );

  const pinnedTools = useMemo(
    () =>
      pinnedMenuTools.reduce<SidebarItem[]>((acc, itemKey) => {
        const tool = allItems.find((item) => item.itemKey === itemKey);
        if (tool) acc.push(tool);
        return acc;
      }, []),
    [pinnedMenuTools, allItems]
  );

  const unPinnedTools = useMemo(
    () => allItems.filter((item) => !pinnedMenuTools.includes(item.itemKey)),
    [allItems, pinnedMenuTools]
  );

  const taskBoardSidebarItems = useMemo<SidebarItem[]>(
    () => [
      ...boards.map((board) => ({
        type: "link" as const,
        id: `task-board-${board.id}`,
        href: generateUrl("tasksTasks", { boardId: board.id }),
        title: board.name,
        active:
          activeTab === TabRoute.tasks &&
          activePath === `tasks/board/${board.id}`,
        customIcon: (
          <BoardAvatar size="md" name={board.name} color={board.color} />
        ),
        unreadCount: moduleUnreadIndicators?.task[board.id],
        itemKey: "tasks:board" as AppMenuItem,
      })),
    ],
    [activeTab, activePath, moduleUnreadIndicators?.task, boards, generateUrl]
  );

  return {
    taskBoardSidebarItems,
    allItems,
    unPinnedTools,
    pinnedTools,
    isSidebarExpanded,
    sidebarWidth,
    debugItem: isAppInDevMode() ? debugItem : undefined,
    updateIsSidebarExpanded,
    updateSidebarWidth,
  };
};
